import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";

import appCss from "../styles.css?url";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { LangSync } from "@/components/LangSync";
import { LanguageSuggestionBanner } from "@/components/LanguageSuggestionBanner";
import { AutoTranslate } from "@/components/AutoTranslate";
import { CookieConsent } from "@/components/CookieConsent";
import { remarketingPageView } from "@/lib/remarketing";
import { useAndroidAppMode } from "@/hooks/useAndroidAppMode";
import { useEffect } from "react";
import { I18nextProvider } from "react-i18next";
import { isEnglishPath } from "@/lib/route-i18n";
import { setEnglishRouteMode } from "@/lib/price-format";
import i18n from "@/i18n";
import { enI18n } from "@/i18n/en-instance";

// CSS applied when Android app-mode is active. Hides purchase / upgrade CTAs
// so the WebView complies with Google Play's payment policy. Web + iOS UAs
// never receive the `.android-app-mode` class, so their pricing/checkout UX
// is untouched.
const ANDROID_APP_MODE_CSS = `
  html.android-app-mode a[href^="/pricing"],
  html.android-app-mode a[href^="/checkout"],
  html.android-app-mode a[href^="/success"],
  html.android-app-mode a[href*="stripe.com"],
  html.android-app-mode a[href*="klarna.com"],
  html.android-app-mode [data-android-hide],
  html.android-app-mode [data-purchase-cta] {
    display: none !important;
  }
`;


function NotFoundComponent() {
  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="font-display text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Sidan kunde inte hittas</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Sidan finns inte eller har flyttats.
        </p>
        <div className="mt-6">
          <a href="/" className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90">
            Till startsidan
          </a>
        </div>
      </div>
    </main>
  );
}

function ErrorComponent({ error, reset }: import("@tanstack/react-router").ErrorComponentProps) {
  console.error(error);
  const router = useRouter();
  // Lightweight client-side error reporting via Plausible custom events.
  // Ger oss visibility på vilka sidor som faktiskt kraschar för riktiga användare.
  if (typeof window !== "undefined") {
    try {
      (window as any).plausible?.("Client Error", {
        props: {
          message: String((error as Error | undefined)?.message ?? "unknown").slice(0, 120),
          path: window.location.pathname,
        },
      });
    } catch {}
  }
  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">Något gick fel</h1>
        <p className="mt-2 text-sm text-muted-foreground">Försök igen eller gå till startsidan. Om problemet kvarstår, mejla oss på info@alkoholprovet.se.</p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => { router.invalidate(); reset(); }}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Försök igen
          </button>
          <a href="/" className="rounded-md border border-input bg-background px-4 py-2 text-sm font-medium hover:bg-accent/10">
            Startsida
          </a>
        </div>
      </div>
    </main>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => {
    const measurementId = import.meta.env.VITE_LOVABLE_CONNECTOR_GOOGLE_ANALYTICS_API_KEY || "G-T4PETSY74E";
    return {
      meta: [
        { charSet: "utf-8" },
        { name: "viewport", content: "width=device-width, initial-scale=1, viewport-fit=cover" },
        { name: "theme-color", content: "#0f1b3d" },
        { title: "Kunskapsprovet serveringstillstånd 2026 – öva online" },
        { name: "description", content: "Öva inför kunskapsprovet för serveringstillstånd med riktiga frågor och provsimulering på tid. Klara provet på första försöket. Från 199 kr inkl. moms." },
        { name: "author", content: "Alkoholprovet" },
        { property: "og:site_name", content: "Alkoholprovet" },
        { property: "og:title", content: "Kunskapsprovet serveringstillstånd 2026 – öva online" },
        { property: "og:description", content: "Öva inför kunskapsprovet för serveringstillstånd med riktiga frågor och provsimulering på tid. Klara provet på första försöket. Från 199 kr inkl. moms." },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: "Kunskapsprovet serveringstillstånd 2026 – öva online" },
        { name: "twitter:description", content: "Öva inför kunskapsprovet för serveringstillstånd med riktiga frågor och provsimulering på tid. Klara provet på första försöket. Från 199 kr inkl. moms." },
        { property: "og:image", content: "https://alkoholprovet.se/og/default.jpg" },
        { name: "twitter:image", content: "https://alkoholprovet.se/og/default.jpg" },
        { name: "google-site-verification", content: "TGBBsaoKXp0FEVTos7CfTOpz2L9AZdVWNOfono7RHDE" },
      ],
      links: [
        { rel: "stylesheet", href: appCss },
        { rel: "manifest", href: "/manifest.webmanifest" },
        { rel: "icon", type: "image/png", href: "/favicon.png" },
        { rel: "apple-touch-icon", href: "/icon-192.png" },
        { rel: "preconnect", href: "https://js.stripe.com" },
        { rel: "preconnect", href: "https://api.stripe.com" },
        { rel: "dns-prefetch", href: "https://plausible.io" },
        { rel: "dns-prefetch", href: "https://www.googletagmanager.com" },
        // Fonterna är självhostade (se @font-face i src/styles.css). Vi
        // preloadar bara latin-varianterna som används ovanför mitten;
        // latin-ext hämtas bara om ett tecken kräver det.
        { rel: "preload", as: "font", type: "font/woff2", href: "/fonts/sora-latin.woff2", crossOrigin: "anonymous" },
        { rel: "preload", as: "font", type: "font/woff2", href: "/fonts/manrope-latin.woff2", crossOrigin: "anonymous" },
      ],
      scripts: [
        // Google Consent Mode v2 + gtag-bootstrap. I app-läget (?app=1 eller
        // sessionStorage-markören) initieras varken dataLayer, gtag, GA4 eller
        // Google Ads – och gtag.js-biblioteket laddas inte alls. Detta krävs
        // eftersom Googles tag-config för vårt GA4-mät-ID annars automatiskt
        // hämtar in AW-* som "linked destination". Utanför app-läget initieras
        // Consent Mode default-denied och GA4 + Ads konfigureras som vanligt.
        //
        // Prestanda: consent-defaults och alla gtag()-anrop körs direkt och
        // köas i dataLayer precis som förut, men själva biblioteken (gtag.js
        // och Plausible) injiceras först vid första interaktionen (scroll,
        // klick, touch, tangent), vid idle efter 2,5 s, senast efter 4 s. Ingen
        // mätning tappas – kön skickas när biblioteket laddat. Detta flyttar
        // ~1,5 s tredjeparts-JS bort från kritisk väg på mobil.
        {
          children:
            `(function(){var K='lovable:app-mode';var IAP='alkoholprovet:native_iap';var app=false;var iap=false;try{var p=new URLSearchParams(window.location.search);app=p.get('app')==='1';iap=app&&p.get('native_iap')==='apple-v1';}catch(e){}if(app){try{window.sessionStorage.setItem(K,'1');}catch(e){}}else{try{app=window.sessionStorage.getItem(K)==='1';}catch(e){}}if(iap){try{window.sessionStorage.setItem(IAP,'apple-v1');}catch(e){}}window.__alkoholprovetAppMode=app;window.plausible=window.plausible||function(){(window.plausible.q=window.plausible.q||[]).push(arguments)};var plLoaded=false;function loadPl(){if(plLoaded)return;plLoaded=true;var pl=document.createElement('script');pl.defer=true;pl.setAttribute('data-domain','alkoholprovet.se');pl.src='https://plausible.io/js/script.tagged-events.outbound-links.js';document.head.appendChild(pl);}var gLoaded=false;function loadG(){if(gLoaded||app)return;gLoaded=true;var g=document.createElement('script');g.async=true;g.src='https://www.googletagmanager.com/gtag/js?id=${measurementId}';document.head.appendChild(g);}function load(){loadPl();loadG();}var evts=['pointerdown','keydown','scroll','touchstart'];function onEvt(){evts.forEach(function(e){window.removeEventListener(e,onEvt)});load();}evts.forEach(function(e){window.addEventListener(e,onEvt,{once:true,passive:true})});function onHide(){if(document.visibilityState==='hidden')load();}document.addEventListener('visibilitychange',onHide);window.addEventListener('pagehide',load);setTimeout(loadPl,1200);if(!app){window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}window.gtag=gtag;gtag('consent','default',{ad_storage:'denied',ad_user_data:'denied',ad_personalization:'denied',analytics_storage:'denied',wait_for_update:500});gtag('js',new Date());gtag('set',{currency:'SEK'});gtag('config','${measurementId}',{currency:'SEK'});gtag('config','AW-18146160856',{currency:'SEK'});setTimeout(function(){if('requestIdleCallback' in window){requestIdleCallback(loadG,{timeout:1500});}else{loadG();}},2500);}})();`,
        },

        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Alkoholprovet",
            alternateName: "Alkoholprovet.se",
            url: "https://alkoholprovet.se",
            logo: "https://alkoholprovet.se/icon-192.png",
            description:
              "Alkoholprovet hjälper dig att klara kunskapsprovet för serveringstillstånd med träningsfrågor, provsimulering och förklaringar enligt alkohollagen.",
            email: "info@alkoholprovet.se",
            areaServed: "SE",
            inLanguage: "sv-SE",
            contactPoint: {
              "@type": "ContactPoint",
              contactType: "customer support",
              email: "info@alkoholprovet.se",
              availableLanguage: ["Swedish", "English"],
            },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "Alkoholprovet",
            url: "https://alkoholprovet.se",
            inLanguage: "sv-SE",
            publisher: { "@type": "Organization", name: "Alkoholprovet", url: "https://alkoholprovet.se" },
            potentialAction: {
              "@type": "SearchAction",
              target: {
                "@type": "EntryPoint",
                urlTemplate: "https://alkoholprovet.se/fraga?q={search_term_string}",
              },
              "query-input": "required name=search_term_string",
            },
          }),
        },
      ],
    };
  },
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  // Cast: duplicate router-core type versions after the security upgrade make the props type mismatch.
  errorComponent: ErrorComponent as any,
});

function RootShell({ children }: { children: React.ReactNode }) {
  const english = useRouterState({ select: (s) => isEnglishPath(s.location.pathname) });
  return (
    <html lang={english ? "en" : "sv"}>
      <head><HeadContent /></head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}


function AnalyticsTracker() {
  const router = useRouter();
  useEffect(() => {
    const path = router.state.location.pathname;
    remarketingPageView(path);
    // GA4 – virtuell sidvisning i SPA
    if (typeof window !== "undefined") {
      try {
        (window as any).gtag?.("event", "page_view", {
          page_path: path,
          page_location: window.location.href,
          page_title: document.title,
        });
      } catch {}
    }
  }, [router.state.location.pathname]);
  return null;
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const english = useRouterState({ select: (s) => isEnglishPath(s.location.pathname) });
  setEnglishRouteMode(english);
  useAndroidAppMode();
  return (
    <I18nextProvider i18n={english ? enI18n : i18n}>
    <QueryClientProvider client={queryClient}>
      <style dangerouslySetInnerHTML={{ __html: ANDROID_APP_MODE_CSS }} />
      <LangSync />
      <AutoTranslate />
      <AnalyticsTracker />
      <div className="min-h-screen flex flex-col">
        <LanguageSuggestionBanner />
        <SiteHeader />
        <main className="flex-1">
          <Outlet />
        </main>
        <SiteFooter />
      </div>
      <CookieConsent />
    </QueryClientProvider>
    </I18nextProvider>
  );
}
